
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { styled, Typography } from '@mui/material';
import {
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    GroupedField,
    GroupedFields,
    FormBreadcrumbs,
    GroupedTableArrayField,
    ICrfConditionParameters,
    IGroupedTableArrayFieldColumn,
    Input,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
    ToggleButtonGroup,
    FormOptionsContext,
    getParentPropertyPath
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { FC, FunctionComponent, useContext, useEffect, useMemo } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../../../api/dtos';
import { ConditionMode, useFieldActions, useFieldState, useFormActions, ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IAdditionalCancerFormProps { }

interface IAdditionalCancerDetailFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */
const StyledSubheading = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3, 2, 3, 2)
    
}))


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const undetectedCancersRemoved = ({ formState }: ICrfConditionParameters<Dtos.AdditionalCancer>) => formState?.value?.undetectedCancersRemoved === Dtos.LookupYesNoType.Yes;
const histopathologicTypeSpecify = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.AdditionalCancer>) =>  {
    const regex = /additionalCancerDetails\[(?<Index>[0-9]*)]/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const histoType = formState?.value?.additionalCancerDetails?.[+idx]?.histopathologicType;

    return histoType === Dtos.LookupHistopathologyType.Other;
}

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */
const SpecifyUpdater: FC = () => {
    const { readOnly } = useContext(FormOptionsContext);
    const { value: histopathologicType } = useFieldState<Dtos.LookupHistopathologyType>();
    const { getPropertyPath } = useFieldActions();
    
    const parentPropertyPath = useMemo(() => {
        return getParentPropertyPath(getPropertyPath());
    }, [getPropertyPath]);

    const {setFieldValue} = useFormActions();

    useEffect(() => {
        if (histopathologicType !== Dtos.LookupHistopathologyType.Other && !readOnly) {
            setFieldValue(`${parentPropertyPath}.histopathologicTypeSpecify`, undefined, true, true, true, false)
        }
    }, [histopathologicType, parentPropertyPath, readOnly])

    return null;
}

const AdditionalCancerDetailColumns: Array<IGroupedTableArrayFieldColumn<Dtos.AdditionalCancerDetail>> = [
    {
        propertyPath: 'additionalTumourSide',
        TableCellProps: {
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'additionalTumourSize',
        TableCellProps: {
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <Input
                component={Numeric}
            />
        )
    },
    {
        propertyPath: 'histopathologicType',
        TableCellProps: {
            sx: {
                minWidth: 220,
                maxWidth: 220,
                width: 220
            }
        },
        content: (
            <>
                <SpecifyUpdater />
                <Input
                    component={Select}
                />
            </>
        )
    },
    {
        propertyPath: 'histopathologicTypeSpecify',
        TableCellProps: {
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={histopathologicTypeSpecify}
                subscription={{ value: true }}
            >
                <Input
                    component={Text}
                />
            </CrfCondition>
        )
    },
    {
        propertyPath: 'histologicGrade',
        TableCellProps: {
            sx: {
                minWidth: 180,
                maxWidth: 180,
                width: 180
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'multifocality',
        TableCellProps: {
            sx: {
                minWidth: 260,
                maxWidth: 260,
                width: 260
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lymphovascularInvasionPresent',
        TableCellProps: {
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <Input
                component={ToggleButtonGroup}
            />
        )
    },
    {
        propertyPath: 'additionalDCISUnrelated',
        TableCellProps: {
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <Input
                component={ToggleButtonGroup}
            />
        )
    },
    {
        propertyPath: 'estrogenReceptor',
        TableCellProps: {
            sx: {
                minWidth: 180,
                maxWidth: 180,
                width: 180
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'progesteroneReceptor',
        TableCellProps: {
            sx: {
                minWidth: 180,
                maxWidth: 180,
                width: 180
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'her2Ihc',
        TableCellProps: {
            sx: {
                minWidth: 180,
                maxWidth: 180,
                width: 180
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'her2Ish',
        TableCellProps: {
            sx: {
                minWidth: 180,
                maxWidth: 180,
                width: 180
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    }
];

const rowName = () => "these details";

const AdditionalCancerDetailForm: FunctionComponent<IAdditionalCancerDetailFormProps> = () => {    
    return (
        <>
            <StyledSubheading
                variant="h2"
            >
                Additional cancer details
            </StyledSubheading>
            <GroupedTableArrayField
                propertyPath="additionalCancerDetails"
                columns={AdditionalCancerDetailColumns}
                entityName="Details"
                getRowName={rowName}
                AddButtonProps={{
                    sx: {
                        mb: 2
                    }
                }}
                //rowLabel="Relevant Medical History/Ongoing Condition"
            />
        </>
    );
}

const AdditionalCancerForm: FunctionComponent<IAdditionalCancerFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
            >
                <GroupedField 
                    propertyPath="undetectedCancersRemoved"
                    component={ToggleButtonGroup}
                />

                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={undetectedCancersRemoved}
                    subscription={{ value: true }}
                >
                    <GroupedFields>
                        <AdditionalCancerDetailForm />
                    </GroupedFields>
                </CrfCondition>
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default AdditionalCancerForm;
